import { isSupabaseConfigured, supabase } from "./supabase";
import {
  fallbackArticles,
  fallbackContact,
  fallbackOffice,
  fallbackProfile,
  fallbackResources,
  fallbackServices,
} from "./content";

const missingRelationCodes = new Set(["42P01", "PGRST204", "PGRST205"]);

function sortByNumber(rows, field) {
  return [...rows].sort((a, b) => Number(a?.[field] ?? 999) - Number(b?.[field] ?? 999));
}

function sortNewest(rows) {
  return [...rows].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
}

function errorMessage(error) {
  return error?.message || "Gabim i panjohur në lidhjen me Supabase.";
}

async function readFirstAvailable(tableNames) {
  if (!supabase) return { rows: [], table: null, error: null };

  let lastError = null;
  for (const table of tableNames) {
    const { data, error } = await supabase.from(table).select("*");
    if (!error) return { rows: data || [], table, error: null };
    lastError = error;
    if (!missingRelationCodes.has(error.code)) break;
  }

  return { rows: [], table: null, error: lastError };
}

export async function loadPublicContent() {
  if (!supabase) {
    return {
      connected: false,
      connectionWarning: "Supabase nuk është konfiguruar. Po shfaqet përmbajtja e sigurt rezervë.",
      profile: fallbackProfile,
      office: fallbackOffice,
      contact: fallbackContact,
      branding: null,
      services: fallbackServices,
      resources: fallbackResources,
      articles: fallbackArticles,
    };
  }

  const [profileResult, officeResult, contactResult, brandingResult, servicesResult, resourcesResult, articlesResult] =
    await Promise.all([
      readFirstAvailable(["lawyer_profile", "profiles"]),
      readFirstAvailable(["office_settings"]),
      readFirstAvailable(["contact_settings"]),
      readFirstAvailable(["branding"]),
      readFirstAvailable(["services"]),
      readFirstAvailable(["legal_resources", "legal_links"]),
      readFirstAvailable(["articles"]),
    ]);

  const profile = { ...fallbackProfile, ...(profileResult.rows[0] || {}) };
  const office = { ...fallbackOffice, ...(officeResult.rows[0] || {}) };
  const contact = {
    ...fallbackContact,
    phone: profile.phone || office.phone || fallbackContact.phone,
    email: profile.email || office.email || fallbackContact.email,
    ...(contactResult.rows[0] || {}),
  };
  const branding = brandingResult.rows.find((item) => item.is_active) || brandingResult.rows[0] || null;
  const services = sortByNumber(servicesResult.rows.filter((item) => item.active !== false), "sort_order");
  const resources = sortByNumber(resourcesResult.rows.filter((item) => item.active !== false), "sort_order");
  const articles = sortNewest(articlesResult.rows.filter((item) => item.published !== false)).slice(0, 3);
  const errors = [profileResult, officeResult, contactResult, brandingResult, servicesResult, resourcesResult, articlesResult]
    .filter((result) => result.error)
    .map((result) => errorMessage(result.error));

  return {
    connected: true,
    connectionWarning: errors.length ? "Disa tabela nuk ishin të lexueshme; për ato pjesë po përdoret përmbajtja rezervë." : "",
    profile,
    office,
    contact,
    branding,
    services: services.length ? services : fallbackServices,
    resources: resources.length ? resources : fallbackResources,
    articles: articles.length ? articles : fallbackArticles,
  };
}

export async function submitEnquiryWithDocuments(form, documents, formElapsedMs) {
  const payload = new FormData();
  payload.set("name", form.name.trim());
  payload.set("phone", form.phone.trim());
  payload.set("email", form.email.trim());
  payload.set("service", form.service || "");
  payload.set("message", form.message.trim());
  payload.set("preferredLanguage", form.preferredLanguage || "sq");
  payload.set("company", form.company || "");
  payload.set("formElapsedMs", String(formElapsedMs || 0));
  if (form.preferredDate) payload.set("preferredDate", new Date(form.preferredDate).toISOString());
  documents.forEach((document) => payload.append("documents", document, document.name));

  const response = await fetch("/api/enquiries", {
    method: "POST",
    body: payload,
    headers: { Accept: "application/json" },
  });

  let result = null;
  try {
    result = await response.json();
  } catch {
    // A JSON response is expected from the Pages Function.
  }
  if (!response.ok || !result?.ok) {
    throw new Error(result?.error || "Shërbimi i ngarkimit të dokumenteve nuk u përgjigj siç pritej.");
  }
  return result;
}

export async function getDocumentDownloadUrl(attachment) {
  if (!supabase) throw new Error("Supabase nuk është konfiguruar në .env.");
  if (attachment?.bucket && attachment.bucket !== "client-documents") {
    throw new Error("Dokumenti nuk i përket hapësirës private të lejuar.");
  }
  const path = String(attachment?.path || "");
  if (!path || path.startsWith("/") || path.includes("..")) throw new Error("Rruga e dokumentit nuk është e vlefshme.");

  const downloadName = String(attachment?.name || "dokument").slice(0, 180);
  const { data, error } = await supabase.storage
    .from("client-documents")
    .createSignedUrl(path, 60, { download: downloadName });
  if (error) throw error;
  if (!data?.signedUrl) throw new Error("Lidhja private e dokumentit nuk u krijua.");
  return data.signedUrl;
}

export async function getSession() {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export function onAuthChange(callback) {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(session));
  return () => data.subscription.unsubscribe();
}

export async function signIn(email, password) {
  if (!supabase) throw new Error("Supabase nuk është konfiguruar në .env.");
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.session;
}

export async function signOut() {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function verifyAdmin(user) {
  if (!supabase || !user) return { allowed: false, reason: "Nuk ka sesion aktiv." };

  const roleResult = await supabase.from("user_roles").select("role").eq("user_id", user.id).maybeSingle();
  if (!roleResult.error && roleResult.data?.role === "admin") return { allowed: true, source: "user_roles" };

  const profileById = await supabase.from("profiles").select("role").eq("id", user.id).maybeSingle();
  if (!profileById.error && profileById.data?.role === "admin") return { allowed: true, source: "profiles" };

  if (user.email) {
    const profileByEmail = await supabase.from("profiles").select("role").eq("email", user.email).maybeSingle();
    if (!profileByEmail.error && profileByEmail.data?.role === "admin") return { allowed: true, source: "profiles" };
  }

  const reason = roleResult.error
    ? `Roli admin nuk mund të verifikohej: ${roleResult.error.message}`
    : "Ky përdorues nuk ka rolin admin.";
  return { allowed: false, reason };
}

export async function loadAdminData() {
  const [messages, consultations, services, resources, articles, profile, branding, cases, tasks, notes, caseDocuments, caseEvents, staff, firmSettings, invoices, invoiceItems, timeEntries] = await Promise.all([
    readFirstAvailable(["website_messages", "messages"]),
    readFirstAvailable(["appointments", "consultations"]),
    readFirstAvailable(["services"]),
    readFirstAvailable(["legal_resources", "legal_links"]),
    readFirstAvailable(["articles"]),
    readFirstAvailable(["lawyer_profile", "profiles"]),
    readFirstAvailable(["branding"]),
    readFirstAvailable(["cases"]),
    readFirstAvailable(["case_tasks"]),
    readFirstAvailable(["case_notes"]),
    readFirstAvailable(["case_documents"]),
    readFirstAvailable(["case_events"]),
    readFirstAvailable(["user_roles"]),
    readFirstAvailable(["firm_settings"]),
    readFirstAvailable(["invoices"]),
    readFirstAvailable(["invoice_items"]),
    readFirstAvailable(["case_time_entries"]),
  ]);

  return {
    messages: { ...messages, rows: sortNewest(messages.rows) },
    consultations: { ...consultations, rows: sortNewest(consultations.rows) },
    services: { ...services, rows: sortByNumber(services.rows, "sort_order") },
    resources: { ...resources, rows: sortByNumber(resources.rows, "sort_order") },
    articles: { ...articles, rows: sortNewest(articles.rows) },
    profile,
    branding,
    cases: { ...cases, rows: sortNewest(cases.rows.map((row) => ({ ...row, created_at: row.updated_at || row.created_at }))) },
    tasks: { ...tasks, rows: sortNewest(tasks.rows) },
    notes: { ...notes, rows: sortNewest(notes.rows) },
    caseDocuments: { ...caseDocuments, rows: sortNewest(caseDocuments.rows) },
    caseEvents: { ...caseEvents, rows: sortNewest(caseEvents.rows.map((row) => ({ ...row, created_at: row.occurred_at || row.created_at }))) },
    staff: { ...staff, rows: staff.rows.filter((row) => row.active !== false) },
    firmSettings,
    invoices: { ...invoices, rows: sortNewest(invoices.rows.map((row) => ({ ...row, created_at: row.issue_date || row.created_at }))) },
    invoiceItems,
    timeEntries: { ...timeEntries, rows: sortNewest(timeEntries.rows.map((row) => ({ ...row, created_at: row.entry_date || row.created_at }))) },
  };
}

export async function saveInvoice(invoice, items) {
  if (!supabase) throw new Error("Supabase nuk është konfiguruar në .env.");
  const { data, error } = await supabase.rpc("admin_save_invoice", {
    p_invoice: invoice,
    p_items: items,
  });
  if (error) throw error;
  const saved = Array.isArray(data) ? data[0] : data;
  if (!saved?.id) throw new Error("Fatura nuk u ruajt siç pritej.");
  return saved;
}

export async function insertRow(table, values) {
  if (!supabase || !table) throw new Error("Tabela e Supabase nuk është e disponueshme.");
  const { data, error } = await supabase.from(table).insert(values).select().single();
  if (error) throw error;
  return data;
}

export async function updateRow(table, id, values) {
  if (!supabase || !table) throw new Error("Tabela e Supabase nuk është e disponueshme.");
  const { data, error } = await supabase.from(table).update(values).eq("id", id).select().maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteRow(table, id) {
  if (!supabase || !table) throw new Error("Tabela e Supabase nuk është e disponueshme.");
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw error;
}

export { isSupabaseConfigured };
